import React, {FC, useState} from 'react';
import {Button, ButtonProps, Spinner} from 'reactstrap';
import OverlayWithSpinner from './OverlayWithSpinner.tsx';
import {useConfirm} from '../ds/confirmContext.ts';

interface ConfirmButtonProps extends ButtonProps {
    // The question the dialog asks: 'Delete "Pick a path story"?'
    message: string;
    // What happens if they go ahead.
    body?: React.ReactNode;
    confirmLabel?: string;
    safeLabel?: string;
    // Off for confirmations that aren't destructive.
    danger?: boolean;
    typeToConfirm?: string;
    submittingText?: string;
    cssClass?: string;
    onClick: (
        event: React.MouseEvent<HTMLButtonElement>
    ) => Promise<void> | void;
}

/*
 * A button that asks first. The spinner only starts once the person has
 * said yes, so the dialog itself is never covered by it.
 */
const ConfirmButton: FC<ConfirmButtonProps> = ({
    message,
    body,
    confirmLabel = 'Confirm',
    safeLabel,
    danger = true,
    typeToConfirm,
    submittingText = 'Submitting...',
    cssClass,
    onClick,
    children,
    disabled,
    ...buttonProps
}) => {
    const confirm = useConfirm();
    const [isSubmitting, setIsSubmitting] = useState(false);

    const onConfirm = async (event: React.MouseEvent<HTMLButtonElement>) => {
        const confirmed = await confirm({
            title: message,
            body,
            confirmLabel,
            safeLabel,
            danger,
            typeToConfirm
        });
        if (!confirmed) {
            return;
        }

        const result = onClick(event);
        if (result instanceof Promise) {
            setIsSubmitting(true);
            try {
                await result;
            } finally {
                setIsSubmitting(false);
            }
        }
    };

    const isActionLink = cssClass?.includes('action-link');
    // A cssClass like "btn btn-danger" names the colour; hand that to
    // reactstrap rather than letting its default btn-secondary sit beside it.
    const colourFromClass = cssClass?.match(
        /\bbtn-(primary|secondary|tertiary|danger|link)\b/
    )?.[1];
    const className = cssClass
        ?.replace(/\bbtn(-(primary|secondary|tertiary|danger|link))?\b/g, '')
        .trim();

    return (
        <>
            <Button
                {...buttonProps}
                type="button"
                disabled={disabled || isSubmitting}
                onClick={event => void onConfirm(event)}
                color={
                    isActionLink
                        ? undefined
                        : buttonProps.color ?? colourFromClass ?? 'secondary'
                }
                className={className || buttonProps.className}
            >
                {isSubmitting ? (
                    <>
                        <Spinner size="sm" />
                        {submittingText}
                    </>
                ) : (
                    children
                )}
            </Button>
            {isSubmitting && <OverlayWithSpinner />}
        </>
    );
};

export default ConfirmButton;
